import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection, RecordType } from '@factorialco/f0-react/dist/experimental'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { JobCatalogPeopleCharts } from 'modules/job_catalog/components/JobCatalogPeopleCharts'
import { getChildTypeText, normalizeNodeType } from 'modules/job_catalog/lib/nodesUtils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { useJobCatalogPeople } from './useJobCatalogPeople'
import { usePeopleInsights } from './usePeopleInsights'

const PeoplePage = () => {
  const { rootId, familyId, functionId, roleId, levelId } = useParams()
  const id = levelId || roleId || functionId || familyId || rootId || ''
  const { source, avatars, isLevel } = useJobCatalogPeople(id)
  const { data: insights, loading: insightsLoading } = usePeopleInsights(id)

  useEffect(() => {
    if (!id) return
    track(trackingEvents.jobCatalog.navigateNodeSubmit, {
      event_category: 'Organizations',
      node_type: normalizeNodeType(id),
      node_id: id,
      tab: 'people',
    })
  }, [id])
  const groupedText = getChildTypeText(id).toLowerCase()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  return (
    <ResponsivePage.Body variant="full">
      {(insightsLoading || (insights && insights.totalCount > 0)) && (
        <Box paddingY="s20">
          <JobCatalogPeopleCharts
            insights={insights}
            loading={insightsLoading}
            groupedBy={groupedText}
            isLevel={isLevel}
          />
        </Box>
      )}
      <OneDataCollection
        source={source}
        emptyStates={{
          'no-data': {
            emoji: '🕵️',
            title: i18n.t('job_catalog.nodes.empty_state.people.title'),
            description: i18n.t('job_catalog.nodes.empty_state.people.description'),
            actions: [
              {
                label: i18n.t('job_catalog.nodes.empty_state.people.actions.navigate'),
                onClick: () => navigate(navigation.employees.fullPath()),
                variant: 'outline',
              },
            ],
          },
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('job_catalog.nodes.people.employee_name'),
                  render: (record: RecordType) => {
                    if (typeof record.employeeAccessId !== 'string')
                      return {
                        type: 'person' as const,
                        value: {
                          firstName: '',
                          lastName: '',
                          src: undefined,
                        },
                      }
                    return {
                      type: 'person' as const,
                      value: {
                        firstName: record.employeeFullName,
                        lastName: '',
                        src: buildUrl(avatars[record.employeeAccessId]),
                      },
                    }
                  },
                },
                {
                  label: i18n.t('job_catalog.nodes.people.role'),
                  render: (record: RecordType) => ({
                    type: 'tag',
                    value: {
                      label: record.roleName,
                    },
                  }),
                },
                {
                  label: i18n.t('job_catalog.nodes.people.level'),
                  render: (record: RecordType) => ({
                    type: 'tag',
                    value: {
                      label: record.levelName,
                    },
                  }),
                },
              ],
              frozenColumns: 1,
            },
          },
        ]}
      />
    </ResponsivePage.Body>
  )
}

export default PeoplePage
